import { useState } from 'react'
import { V2CatalogSupplierFunnelStatusCell } from '@/v2/components/V2CatalogSupplierFunnelStatusCell'
import { V2TableLink } from '@/v2/components/V2TableLink'
import { getV2ComponentDetailPath, V2_SUPPLIERS_PATH } from '@/v2/utils/routes'
import { Link2, Loader2, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { toast } from 'sonner'
import {
  useDeleteCatalogSupplier,
  useGetCatalogSupplier,
  useUpdateCatalogSupplier,
} from '@/shared/hooks/api/catalog'
import { cn } from '@/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { V2CatalogSupplierInviteDialog } from '@/v2/components/V2CatalogSupplierInviteDialog'

type SupplierEditableField = 'name' | 'address' | 'contactName' | 'contactEmail'

function isValidOptionalEmail(value: string): boolean {
  if (value.length === 0) {
    return true
  }
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function formatOptionalText(value: string | null, fallback: string): string {
  if (value != null && value.trim() !== '') {
    return value.trim()
  }
  return fallback
}

export default function V2SupplierDetailPage() {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { supplierId } = useParams<{ supplierId: string }>()
  const {
    data: supplier,
    isLoading,
    isError,
  } = useGetCatalogSupplier(supplierId)
  const updateSupplierMutation = useUpdateCatalogSupplier(supplierId ?? '')
  const deleteSupplierMutation = useDeleteCatalogSupplier()

  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false)
  const [editingField, setEditingField] =
    useState<SupplierEditableField | null>(null)
  const [editValue, setEditValue] = useState('')

  async function handleDeleteSupplier() {
    if (supplierId == null || supplierId.length === 0) {
      return
    }
    await deleteSupplierMutation.mutateAsync(supplierId)
    setIsDeleteConfirmOpen(false)
    navigate(V2_SUPPLIERS_PATH)
  }

  function openFieldEditor(field: SupplierEditableField) {
    if (supplier == null) {
      return
    }
    if (field === 'name') {
      setEditValue(supplier.name)
    } else if (field === 'address') {
      setEditValue(supplier.address ?? '')
    } else if (field === 'contactName') {
      setEditValue(supplier.contactName ?? '')
    } else {
      setEditValue(supplier.contactEmail ?? '')
    }
    setEditingField(field)
  }

  function closeFieldEditor() {
    setEditingField(null)
    setEditValue('')
  }

  let fieldEditorTitle = t('v2.pages.suppliers.detail.editContactEmailTitle')
  let fieldEditorLabel = t('v2.pages.suppliers.detail.contactEmail')
  if (editingField === 'name') {
    fieldEditorTitle = t('v2.pages.suppliers.detail.editNameTitle')
    fieldEditorLabel = t('v2.pages.suppliers.detail.nameLabel')
  } else if (editingField === 'address') {
    fieldEditorTitle = t('v2.pages.suppliers.detail.editAddressTitle')
    fieldEditorLabel = t('v2.pages.suppliers.detail.addressLabel')
  } else if (editingField === 'contactName') {
    fieldEditorTitle = t('v2.pages.suppliers.detail.editContactNameTitle')
    fieldEditorLabel = t('v2.pages.suppliers.detail.contactNameLabel')
  }

  async function handleSaveField() {
    if (editingField == null || supplierId == null) {
      return
    }
    const trimmed = editValue.trim()
    if (editingField === 'name') {
      if (trimmed.length === 0 || trimmed === supplier?.name) {
        return
      }
      await updateSupplierMutation.mutateAsync({ name: trimmed })
      toast.success(t('v2.pages.suppliers.detail.nameSaveSuccess'))
    } else if (editingField === 'address') {
      const nextAddress = trimmed.length > 0 ? trimmed : null
      if (nextAddress === (supplier?.address?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({ address: nextAddress })
      toast.success(t('v2.pages.suppliers.detail.addressSaveSuccess'))
    } else if (editingField === 'contactName') {
      const nextContactName = trimmed.length > 0 ? trimmed : null
      if (nextContactName === (supplier?.contactName?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({ contactName: nextContactName })
      toast.success(t('v2.pages.suppliers.detail.contactNameSaveSuccess'))
    } else {
      if (!isValidOptionalEmail(trimmed)) {
        toast.error(t('v2.pages.suppliers.detail.invalidEmail'))
        return
      }
      const nextContactEmail = trimmed.length > 0 ? trimmed : null
      if (nextContactEmail === (supplier?.contactEmail?.trim() || null)) {
        return
      }
      await updateSupplierMutation.mutateAsync({
        contactEmail: nextContactEmail,
      })
      toast.success(t('v2.pages.suppliers.detail.contactEmailSaveSuccess'))
    }
    closeFieldEditor()
  }

  if (supplierId == null || supplierId.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        {t('v2.pages.suppliers.detail.loadFailed')}
      </p>
    )
  }

  if (isLoading) {
    return (
      <div className='space-y-4' aria-busy='true'>
        <Skeleton className='h-10 w-64' />
        <Skeleton className='h-32 w-full' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  if (isError || supplier == null) {
    return (
      <p className='text-sm text-destructive'>
        {t('v2.pages.suppliers.detail.loadFailed')}
      </p>
    )
  }

  const emptyContact = t('v2.pages.suppliers.detail.noContact')

  return (
    <>
      <div className='space-y-8'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div className='space-y-2'>
            <h1 className='text-2xl font-semibold tracking-tight'>
              {supplier.name}
            </h1>
            <V2CatalogSupplierFunnelStatusCell
              onboardingStatus={supplier.onboardingStatus}
              magicLinkFirstOpenedAt={supplier.magicLinkFirstOpenedAt}
              requestAcknowledgedAt={supplier.requestAcknowledgedAt}
            />
          </div>
          <div className='flex flex-wrap gap-2'>
            <Button
              type='button'
              variant='outline'
              className='gap-2'
              onClick={() => {
                setIsInviteOpen(true)
              }}
            >
              <Link2 className='size-4' />
              {t('v2.pages.suppliers.invite.open')}
            </Button>
            <Button
              type='button'
              variant='destructive'
              className='gap-2'
              onClick={() => {
                setIsDeleteConfirmOpen(true)
              }}
            >
              <Trash2 className='size-4' />
              {t('v2.pages.suppliers.detail.delete')}
            </Button>
          </div>
        </div>

        <div className='grid gap-2 sm:grid-cols-2'>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('v2.pages.suppliers.detail.nameLabel')}</ItemTitle>
              <ItemDescription>{supplier.name}</ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                onClick={() => {
                  openFieldEditor('name')
                }}
                disabled={updateSupplierMutation.isPending}
                aria-label={t('v2.pages.suppliers.detail.editNameAria')}
              >
                <Pencil className='size-4' />
              </Button>
            </ItemActions>
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>
                {t('v2.pages.suppliers.detail.addressLabel')}
              </ItemTitle>
              <ItemDescription>
                {supplier.address?.trim() || emptyContact}
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                onClick={() => {
                  openFieldEditor('address')
                }}
                disabled={updateSupplierMutation.isPending}
                aria-label={t('v2.pages.suppliers.detail.editAddressAria')}
              >
                <Pencil className='size-4' />
              </Button>
            </ItemActions>
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>
                {t('v2.pages.suppliers.detail.contactNameLabel')}
              </ItemTitle>
              <ItemDescription>
                {supplier.contactName?.trim() || emptyContact}
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                onClick={() => {
                  openFieldEditor('contactName')
                }}
                disabled={updateSupplierMutation.isPending}
                aria-label={t('v2.pages.suppliers.detail.editContactNameAria')}
              >
                <Pencil className='size-4' />
              </Button>
            </ItemActions>
          </Item>
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>
                {t('v2.pages.suppliers.detail.contactEmail')}
              </ItemTitle>
              <ItemDescription>
                {supplier.contactEmail?.trim() || emptyContact}
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-8 shrink-0 text-muted-foreground'
                onClick={() => {
                  openFieldEditor('contactEmail')
                }}
                disabled={updateSupplierMutation.isPending}
                aria-label={t('v2.pages.suppliers.detail.editContactEmailAria')}
              >
                <Pencil className='size-4' />
              </Button>
            </ItemActions>
          </Item>
        </div>

        <section className='space-y-3'>
          <div className='space-y-1'>
            <h2 className='text-lg font-semibold'>
              {t('v2.pages.suppliers.detail.productsHeading')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.suppliers.detail.productsDescription')}
            </p>
          </div>
          {supplier.products.length === 0 ? (
            <Empty className='border border-dashed'>
              <EmptyHeader>
                <EmptyTitle>
                  {t('v2.pages.suppliers.detail.emptyProducts.title')}
                </EmptyTitle>
                <EmptyDescription>
                  {t('v2.pages.suppliers.detail.emptyProducts.description')}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className='overflow-hidden rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('v2.pages.suppliers.detail.columns.productName')}
                    </TableHead>
                    <TableHead>
                      {t('v2.pages.suppliers.detail.columns.sku')}
                    </TableHead>
                    <TableHead>
                      {t('v2.pages.suppliers.detail.columns.productModel')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplier.products.map((product) => {
                    const productName = formatOptionalText(
                      product.name,
                      t('v2.pages.products.fallbackTitle')
                    )

                    return (
                      <TableRow key={product.id}>
                        <TableCell
                          className='max-w-0 truncate'
                          title={productName}
                        >
                          {productName}
                        </TableCell>
                        <TableCell className='max-w-0 truncate text-muted-foreground'>
                          {formatOptionalText(product.sku, t('notAvailable'))}
                        </TableCell>
                        <TableCell className='max-w-0 truncate text-muted-foreground'>
                          {formatOptionalText(
                            product.productModel,
                            t('notAvailable')
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>

        <section className='space-y-3'>
          <div className='space-y-1'>
            <h2 className='text-lg font-semibold'>
              {t('v2.pages.suppliers.detail.partsHeading')}
            </h2>
            <p className='text-sm text-muted-foreground'>
              {t('v2.pages.suppliers.detail.partsDescription')}
            </p>
          </div>
          {supplier.components.length === 0 ? (
            <Empty className='border border-dashed'>
              <EmptyHeader>
                <EmptyTitle>
                  {t('v2.pages.suppliers.detail.emptyParts.title')}
                </EmptyTitle>
                <EmptyDescription>
                  {t('v2.pages.suppliers.detail.emptyParts.description')}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <div className='overflow-hidden rounded-md border'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      {t('v2.pages.suppliers.detail.columns.partName')}
                    </TableHead>
                    <TableHead>
                      {t('v2.pages.suppliers.detail.columns.sku')}
                    </TableHead>
                    <TableHead className='text-end'>
                      {t('v2.pages.suppliers.detail.columns.linkedProducts')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {supplier.components.map((component) => {
                    const partName = formatOptionalText(
                      component.name,
                      t('v2.pages.components.fallbackName')
                    )

                    return (
                      <TableRow key={component.id}>
                        <TableCell className='max-w-0 truncate'>
                          <V2TableLink
                            to={getV2ComponentDetailPath(component.id)}
                            truncate
                            title={partName}
                          >
                            {partName}
                          </V2TableLink>
                        </TableCell>
                        <TableCell className='max-w-0 truncate text-muted-foreground'>
                          {component.sku}
                        </TableCell>
                        <TableCell className='text-end text-muted-foreground'>
                          {component.linkedProductCount}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>

      <Dialog
        open={editingField != null}
        onOpenChange={(open) => {
          if (!open) {
            closeFieldEditor()
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{fieldEditorTitle}</DialogTitle>
          </DialogHeader>
          <div className='space-y-2'>
            <Label htmlFor='supplier-field-edit'>{fieldEditorLabel}</Label>
            {editingField === 'address' ? (
              <Textarea
                id='supplier-field-edit'
                value={editValue}
                onChange={(event) => {
                  setEditValue(event.target.value)
                }}
                autoFocus
              />
            ) : (
              <Input
                id='supplier-field-edit'
                type={editingField === 'contactEmail' ? 'email' : 'text'}
                value={editValue}
                onChange={(event) => {
                  setEditValue(event.target.value)
                }}
                autoFocus
              />
            )}
          </div>
          <DialogFooter>
            <Button
              type='button'
              variant='outline'
              onClick={closeFieldEditor}
              disabled={updateSupplierMutation.isPending}
            >
              {t('actions.cancel')}
            </Button>
            <Button
              type='button'
              onClick={() => {
                void handleSaveField()
              }}
              disabled={
                updateSupplierMutation.isPending ||
                (editingField === 'name' &&
                  (editValue.trim().length === 0 ||
                    editValue.trim() === supplier.name)) ||
                (editingField === 'address' &&
                  (editValue.trim() || null) ===
                    (supplier.address?.trim() || null)) ||
                (editingField === 'contactName' &&
                  (editValue.trim() || null) ===
                    (supplier.contactName?.trim() || null)) ||
                (editingField === 'contactEmail' &&
                  ((editValue.trim() || null) ===
                    (supplier.contactEmail?.trim() || null) ||
                    !isValidOptionalEmail(editValue.trim())))
              }
            >
              {updateSupplierMutation.isPending
                ? t('v2.pages.suppliers.detail.saving')
                : t('actions.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <V2CatalogSupplierInviteDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        supplierId={supplier.id}
        magicLinkUrl={supplier.magicLinkUrl}
      />

      <AlertDialog
        open={isDeleteConfirmOpen}
        onOpenChange={(open) => {
          if (deleteSupplierMutation.isPending) {
            return
          }
          setIsDeleteConfirmOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('v2.pages.suppliers.detail.deleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('v2.pages.suppliers.detail.deleteConfirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSupplierMutation.isPending}>
              {t('v2.pages.suppliers.detail.deleteConfirmCancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              className={cn(
                buttonVariants({ variant: 'destructive' }),
                'gap-2'
              )}
              disabled={deleteSupplierMutation.isPending}
              onClick={(event) => {
                event.preventDefault()
                void handleDeleteSupplier()
              }}
            >
              {deleteSupplierMutation.isPending && (
                <Loader2 className='size-4 animate-spin' />
              )}
              {deleteSupplierMutation.isPending &&
                t('v2.pages.suppliers.detail.deleting')}
              {!deleteSupplierMutation.isPending &&
                t('v2.pages.suppliers.detail.deleteConfirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
